import { Component, computed, inject, signal } from '@angular/core';
import { UiSectionHeaderComponent } from '../../shared/components/ui-section-header/ui-section-header.component';
import { EnseigneCardComponent } from './components/enseigne-card/enseigne-card.component';
import { EnseigneFormModalComponent } from './components/enseigne-form-modal/enseigne-form-modal.component';
import { DeleteConfirmationModalComponent } from '../../shared/components/delete-confirmation-modal/delete-confirmation-modal.component';
import { EnseigneConfig } from './enseigne.model';
import { EnseignesStore } from './enseignes.store';
import { TranslateModule } from '@ngx-translate/core';
import { GlobalService } from '../global/global.service';
import { rxResource } from '@angular/core/rxjs-interop';

@Component({
  selector: 'app-enseignes',
  imports: [
    UiSectionHeaderComponent,
    EnseigneCardComponent,
    EnseigneFormModalComponent,
    DeleteConfirmationModalComponent,
    TranslateModule,
  ],
  providers: [EnseignesStore],
  templateUrl: './enseignes.component.html',
  styleUrl: './enseignes.component.scss',
})
export class EnseignesComponent {
  private readonly store = inject(EnseignesStore);
  private readonly globalService = inject(GlobalService);

  private readonly globalConfigResource = rxResource({
    stream: () => this.globalService.getGlobalConfig(),
  });

  // Public computed for template
  readonly isLoading = this.store.isLoading;
  readonly error = this.store.error;
  readonly enseignes = this.store.enseignes;
  readonly availableCountryCodes = computed(() => this.globalConfigResource.value()?.countries ?? []);

  readonly showModal = signal(false);
  readonly selectedEnseigne = signal<EnseigneConfig | null>(null);
  readonly showDeleteModal = signal(false);
  readonly enseigneToDelete = signal<EnseigneConfig | null>(null);

  onAddEnseigne(): void {
    this.selectedEnseigne.set(null);
    this.showModal.set(true);
  }

  onEditEnseigne(enseigne: EnseigneConfig): void {
    this.selectedEnseigne.set(enseigne);
    this.showModal.set(true);
  }

  onDeleteEnseigne(codeOrg: string): void {
    const enseigne = this.enseignes().find((e) => e.codeOrg === codeOrg);
    if (enseigne) {
      this.enseigneToDelete.set(enseigne);
      this.showDeleteModal.set(true);
    }
  }

  onConfirmDelete(): void {
    const enseigne = this.enseigneToDelete();
    if (enseigne) {
      this.store.deleteEnseigne(enseigne.codeOrg);
      this.closeDeleteModal();
    }
  }

  onCancelDelete(): void {
    this.closeDeleteModal();
  }

  onModalSubmit(config: EnseigneConfig): void {
    const isNewEnseigne = !this.selectedEnseigne();

    if (this.selectedEnseigne()) {
      this.store.updateEnseigne(config);
    } else {
      this.store.addEnseigne(config);
    }
    this.closeFormModal();

    // Scroll vers le bas après l'ajout d'une nouvelle enseigne
    if (isNewEnseigne) {
      setTimeout(() => {
        window.scrollTo({ top: document.body.scrollHeight, behavior: 'smooth' });
      }, 100);
    }
  }

  onModalCancel(): void {
    this.closeFormModal();
  }

  private closeDeleteModal(): void {
    this.showDeleteModal.set(false);
    this.enseigneToDelete.set(null);
  }

  private closeFormModal(): void {
    this.showModal.set(false);
    this.selectedEnseigne.set(null);
  }
}
